import { z } from 'zod';

const ftpServerInfoSchema = z.object({
  host: z.string().min(1, 'Host is required'),
  port: z
    .string()
    .min(1, 'Port is required')
    .refine(val => !isNaN(Number(val)), {
      message: 'Port must be a number'
    }),
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  path: z.string().min(1, 'Path is required')
});

const s3ServerInfoSchema = z.object({
  bucket: z.string().min(1, 'Bucket name is required'),
  region: z.string().min(1, 'Region is required'),
  access_key: z.string().min(1, 'Access key is required'),
  secret_key: z.string().min(1, 'Secret key is required'),
  path: z.string().min(1, 'Remote path is required')
});

const gcsServerInfoSchema = z.object({
  bucket: z.string().min(1, 'Bucket name is required'),
  private_key: z.string().min(1, 'Private key is required'),
  client_email: z.string().min(1, 'Client email is required'),
  project_id: z.string().min(1, 'Project ID is required'),
  path: z.string().min(1, 'Path is required')
});

export const exportFormSchema = z
  .object({
    exportName: z
      .string()
      .trim()
      .min(1, 'Export name is required')
      .regex(
        /^[a-zA-Z0-9_-]+(\s[a-zA-Z0-9_-]+)*$/,
        'Export name can only contain letters, numbers, spaces, hyphens, and underscores'
      ),

    exportType: z.enum(['full', 'basic'], {
      required_error: 'Export type is required'
    }),

    configureType: z.enum(['ftp', 'sftp', 's3', 'gcs', 'api'], {
      required_error: 'Configure type is required'
    }),

    serverInfo: z.record(z.any())
  })
  .superRefine((data, ctx) => {
    if (data.configureType === 'ftp' || data.configureType === 'sftp') {
      const result = ftpServerInfoSchema.safeParse(data.serverInfo);
      if (!result.success) {
        result.error.errors.forEach(error => {
          ctx.addIssue({
            path: ['serverInfo', ...error.path],
            message: error.message,
            code: z.ZodIssueCode.custom
          });
        });
      }
    }

    if (data.configureType === 's3') {
      const result = s3ServerInfoSchema.safeParse(data.serverInfo);
      if (!result.success) {
        result.error.errors.forEach(error => {
          ctx.addIssue({
            path: ['serverInfo', ...error.path],
            message: error.message,
            code: z.ZodIssueCode.custom
          });
        });
      }
    }
    if (data.configureType === 'gcs') {
      const result = gcsServerInfoSchema.safeParse(data.serverInfo);
      if (!result.success) {
        result.error.errors.forEach(error => {
          ctx.addIssue({
            path: ['serverInfo', ...error.path],
            message: error.message,
            code: z.ZodIssueCode.custom
          });
        });
      }
    }
  });

export const vendorExportFilterValidation = z.object({
  vendorNames: z.array(z.string()).default([]),
  exportStatus: z.array(z.string()).default([]),
  searchParams: z.string().default(''),
  dateRange: z.array(z.string()).default([])
});

export const vendorExportAdvanceConfigurationSchema = z
  .object({
    exportName: z.array(z.string()).min(1, 'Export name is required'),
    exportBy: z.enum(['manuscriptIds', 'dateRange'], {
      required_error: 'Please select one option'
    }),
    manuscriptIds: z.string().optional(),
    exportStatus: z
      .enum(['all', 'failed'], {
        required_error: 'Please select one option'
      })
      .optional(),
    fromDate: z.string().optional(),
    toDate: z.string().optional()
  })
  .superRefine((data, ctx) => {
    if (data.exportBy === 'manuscriptIds') {
      if (!data.manuscriptIds || data.manuscriptIds.trim() === '') {
        ctx.addIssue({
          path: ['manuscriptIds'],
          message: 'Manuscript ID(s) are required',
          code: z.ZodIssueCode.custom
        });
      }
    }
    if (data.exportBy === 'dateRange') {
      if (!data.exportStatus) {
        ctx.addIssue({
          path: ['exportStatus'],
          message: 'Export status is required',
          code: z.ZodIssueCode.custom
        });
      }
      if (!data.fromDate) {
        ctx.addIssue({
          path: ['fromDate'],
          message: 'From date is required',
          code: z.ZodIssueCode.custom
        });
      }
      if (!data.toDate) {
        ctx.addIssue({
          path: ['toDate'],
          message: 'To date is required',
          code: z.ZodIssueCode.custom
        });
      }
      if (data.fromDate && data.toDate && data.fromDate > data.toDate) {
        ctx.addIssue({
          path: ['toDate'],
          message: 'To date must be after From date',
          code: z.ZodIssueCode.custom
        });
      }
      const now = new Date();
      const fifteenDaysAgo = new Date();
      fifteenDaysAgo.setDate(now.getDate() - 15);

      if (data.fromDate) {
        const from = new Date(data.fromDate);
        if (from < fifteenDaysAgo) {
          ctx.addIssue({
            path: ['fromDate'],
            message: 'From date cannot be earlier than 15 days ago',
            code: z.ZodIssueCode.custom
          });
        }
      }

      if (data.fromDate && data.toDate) {
        const from = new Date(data.fromDate);
        const to = new Date(data.toDate);
        const diffTime = to.getTime() - from.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (diffDays > 15) {
          ctx.addIssue({
            path: ['toDate'],
            message: 'Date range cannot exceed 15 days',
            code: z.ZodIssueCode.custom
          });
        }
      }
      if (data.toDate) {
        const to = new Date(data.toDate);
        const today = new Date();
        to.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        if (to > today) {
          ctx.addIssue({
            path: ['toDate'],
            message: 'To date cannot be later than today',
            code: z.ZodIssueCode.custom
          });
        }
      }
    }
  });
